<script setup lang="ts">
import { ref } from 'vue'
import QuizButton from '@/components/Quiz/QuizButton.vue'
import QuizSecureTag from '@/components/Quiz/QuizSecureTag.vue'
import { useQuizStore } from '@/stores/quizStore.ts'

const { goToNextStep, aagLeadToken } = useQuizStore()
const isValid = ref(false)
const containerRef = ref<HTMLDivElement | null>(null)

const onScrollEnd = (e: HTMLElementEventMap['scroll']) => {
  if (!containerRef.value) {
    return
  }
  const offset = 50;
  const target = e.target as HTMLDivElement
  const bottomPosition = target.scrollTop + target.clientHeight + offset
  const isAtBottom = bottomPosition >= containerRef.value.scrollHeight;
  if (!isValid.value && isAtBottom) {
    isValid.value = true
  }
}

const scrollContainerContentToTheEnd = () => {
  if (containerRef.value) {
    containerRef.value.scrollTo(0, containerRef.value?.scrollHeight);
  }
}
</script>

<template>
  <div
    class="w-full h-[300px] overflow-y-auto scroll-smooth text-sm p-4 pl-6 z-0 [&>*]:z-0 relative flex flex-col gap-4"
    ref="containerRef"
    @scroll="onScrollEnd"
  >
    <div v-if="aagLeadToken.dpf.length" id="dpfFeeCap" class="flex flex-col gap-4 text-justify">
      <p class="uppercase text-center"><strong><em>Understanding your no win, no fee agreement</em></strong></p>
      <ol class="list-decimal">
        <li>Our agreement with you is based on a “No-Win, No-Fee” arrangement. This means that if we do not win your case, <b>and you comply with our terms of business,</b> you will not incur any out-of-pocket expenses. This is known as a “Damages-Based Agreement” (DBA).</li>
        <li>Under a DBA, we commit to handling your claim without charging for our services until the case is fully resolved. Additionally, we ensure that all disbursements are funded, meaning you do not have to pay upfront to pursue your case.</li>
        <li>In exchange for our services, you agree to pay us a percentage of the damages awarded to you (50% inclusive of VAT). If there are additional barristers’ fees, we will cover those with our percentage of the damages taken.</li>
        <li>If your case is successful under a DBA in court, you should be entitled to the majority of your legal costs from the opposing party. These legal costs include our basic charges and any additional expenses we incur on your behalf. We will limit your liability to pay any disbursements to what we recover from your opponent. You will be responsible for any Legal Expense and “After the Event” insurance premiums, however, we front the expense of these as well.</li>
        <li>Any money we recover from the opposing party for our basic fees and barristers’ fees will be refunded to you as a reduction in the amount you need to contribute to us from your awarded compensation.</li>
        <li>If your case does not succeed, you do not need to worry about paying the defendant’s costs or any disbursements we incur on your behalf. We arrange insurance to protect you against these expenses. Additionally, the insurance policy covers its premium in the event that you lose.</li>
      </ol>
      <p class="uppercase"><strong><em>Summary</em></strong></p>
      <p>You will never have to pay out of pocket to finance your DPF claim. You will not have to pay money upfront to finance your claim. If the case is unsuccessful, you won’t need to pay out of your own pocket. In simpler terms, you can rest assured that you won't be burdened with any financial obligations if your case is not successful, so long as you follow the terms and conditions of our agreement.</p>
      <p class="uppercase"><strong><em>Ensuring cooperation – Help us to help you!</em></strong></p>
      <p>We need your ongoing cooperation throughout this process. Given the risk we take under a “No Win, No Fee” agreement, your participation in assisting this process is crucial. If you fail to cooperate, we may discontinue our assistance and stop providing our services. If you decide not to proceed with your claim, please inform us in writing within 14 days of signing up. If you cancel your claim outside of the 14-day probationary period without cause, you may be charged our reasonable costs for the work we have completed building your case to that point.</p>
    </div>
    <hr v-if="aagLeadToken.dpf.length && aagLeadToken.pcp.length" />
    <div v-if="aagLeadToken.pcp.length" id="pcpFeeCap" class="flex flex-col gap-4 text-justify">
      <p v-if="!aagLeadToken.dpf.length" class="uppercase text-center"><strong><em>Good news!</em></strong></p>
      <p class="uppercase text-center"><strong><em>Your case is protected by the SRA Fee Cap!</em></strong></p>
      <p>
        Where your mis-sold finance claim will be pursued through a financial redress scheme (such as a claim via the Financial Ombudsman Service or a statutory redress scheme set up by the Financial Conduct Authority), the amount a legal advisor can charge you for their services is capped by the SRA Regulatory Arrangements (Claims Management Fees) Rules 2024. What you should know:
      </p>
      <ul class="list-disc ml-5">
        <li>There is a realistic prospect of an FCA-led redress scheme being introduced. This scheme would allow claimants to pursue a claim for themselves, free of charge, should they wish to pursue the claim themselves against the finance companies.</li>
        <li>Your rights of termination in your retainer with JLG offers a 14-day cooling off period in case you wish to change your mind -- without charge! After the 14 day period, we retain the right to charge reasonable costs we incur while working on your case.</li>
        <li>JLG can recover up to 30% of your damages for our work on your claims.</li>
      </ul>
      <p>
        For the work we do for you, the amount we can charge you will be dependent upon the size of the compensation you recover.
        It is the lower of either a percentage of your damages or the maximum total charge as set out in the table below.
        These figures include all associated expenses we incur with your claim but do not include VAT.
      </p>
      <img src="@/assets/images/sra_fee_cap.png" alt="sra fee cap table" />
      <p><strong><em>Example</em></strong></p>
      <p>If you recover £1,000 in damages, we will charge you £300 plus VAT (30% of your damages). Total charge = £360 so you will receive £640.</p>
      <p>If you recover £1,499 in damages, the 30% charge would take us to £449.70, which is above the maximum £420 we can charge in that banding.
        Our charges will therefore be restricted to £420 plus VAT (£504). You will get £995 in compensation after deduction of our charges.
      </p>
      <p>We obviously cannot predict exactly how much we will charge until you are awarded compensation.</p>
      <p><strong><em>It’s Your Decision!</em></strong></p>
      <p>You should also be aware that, because your claim is being pursued this way, you are able to bring the claim yourself free of charge
        and without representation if you so wish.
      </p>
      <p>We cannot advise you to use and pay for our services over doing it yourself. Some people prefer to seek the assistance of a legal professional
        so they don’t have to do the work, but others prefer to do it themselves. There is no right or wrong way to do this.
      </p>
      <p>If for any reason we later advise you to pursue your claim by other means where the fee cap no longer applies,
        (such as through the Court) we confirm that we will not take any further action on your behalf until all additional costs consequences
        are fully explained to you and you confirm you wish to proceed on that basis.
      </p>
      <p>Finally, when we send you a bill for our services at the end of your claim we will ensure that any charges that fall outside the fee cap
        are specified on any invoice we send you.
      </p>
    </div>
  </div>

  <div v-if="!isValid" class="w-full text-center h-[24px] continue-bg -mt-6 z-10 relative">
    <button class="inline-block bg-[#ff671d] text-white py-0.5 px-2 text-sm rounded-sm" @click="scrollContainerContentToTheEnd">Continue</button>
  </div>

  <QuizButton
    class="mt-4"
    label="I Acknowledge"
    :disabled="!isValid"
    @click="goToNextStep"
  />

  <QuizSecureTag class="mt-6"/>
</template>

<style scoped>
.continue-bg {
  background: linear-gradient(180deg,rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 100%);
}
</style>
